import React, { type FC, useMemo, useState } from 'react'
import { Box, List, ListItem, ListItemText, Paper, Stack } from '@mui/material'
import useConfig from '@plug-core/hooks/useConfig'
import { useTranslation } from 'react-i18next'
import { FixedSizeList } from 'react-window'
import Touchable from '@components/Touchable'
import SearchBar from '@components/SearchBar/SearchBar'
import { Titlebar } from '@components/Titlebar/Titlebar'
import BottomSheet from '@components/BottomSheet'
import { getWindowDimensions, isSidePanel } from '@utils/windows'
import { POPUP_DIMENSIONS } from '@misc/constants'
import { ExchangeToken } from '@plug-core/interfaces/swapInterface'
import { ETH_SUPPORTED_CHAINS } from '@plug-core/utils/eth'
import TokenAvatar from '@components/TokenAvatar'
import { getChainIcon } from '@plug-core/utils/getChainIcon'

type TokenPickerProps<TokenType extends ExchangeToken = ExchangeToken> = {
  visible: boolean
  tokensForSelect: TokenType[]
  mode: 'swap' | 'convert' | 'stake' | 'mint'
  selectTokenTitle?: string
  onClose: () => void
  onTokenSelect: (token: TokenType | undefined) => void
}

const POPULAR_CONVERT_TOKENS = ['icp', 'btc', 'eth', 'sol', 'xrp']

const TokenPicker = <TokenType extends ExchangeToken = ExchangeToken>({
  visible,
  tokensForSelect,
  mode,
  selectTokenTitle,
  onClose,
  onTokenSelect,
}: TokenPickerProps<TokenType>): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('')

  const { tokens } = useConfig()
  const { t } = useTranslation()

  const { height } = isSidePanel ? getWindowDimensions() : POPUP_DIMENSIONS

  const onSelect = (token: TokenType) => {
    onTokenSelect(token)
    onClose()
  }

  const renderToken = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const token = filteredTokens[index]

    return (
      <Touchable key={index} style={style}>
        <Stack px={2}>
          <Paper
            sx={{ paddingY: 0, minHeight: 50 }}
            elevation={0}
            component={ListItem}
            onClick={() => onSelect(token)}
          >
            <Stack position="relative" mr={2}>
              <TokenAvatar logo={token?.logo} />
              <Stack
                position="absolute"
                right={-2}
                bottom={-6}
                bgcolor="background.paper"
                padding={0.45}
                borderRadius={4}
              >
                {token?.chain && (
                  <img src={getChainIcon(token?.chain || '')} height={14} width={14} />
                )}
              </Stack>
            </Stack>
            <ListItemText
              primary={token.name}
              secondary={token.symbol}
              primaryTypographyProps={{ lineHeight: '18px' }}
              secondaryTypographyProps={{ lineHeight: '18px' }}
            />
          </Paper>
        </Stack>
      </Touchable>
    )
  }

  const filteredTokens = useMemo(() => {
    let tokenScope: TokenType[] = tokensForSelect

    const matchTerm = searchTerm.toLowerCase()
    if (matchTerm) {
      tokenScope = tokenScope.filter(
        (t) =>
          t.name.toLowerCase().includes(matchTerm) || t.symbol.toLowerCase().includes(matchTerm)
      )
    }

    if (mode === 'convert') {
      const getSortValue = (token: ExchangeToken) => {
        if (POPULAR_CONVERT_TOKENS.includes(token.id)) {
          return POPULAR_CONVERT_TOKENS.indexOf(token.id)
        }

        const isErc20Token =
          token.standard === 'eth' &&
          tokens.find(
            (t) =>
              ETH_SUPPORTED_CHAINS.includes(t.chain) &&
              t.standard === 'ERC20' &&
              t.symbol === token.symbol
          )

        if (isErc20Token) {
          return POPULAR_CONVERT_TOKENS.length + 1
        } else {
          return POPULAR_CONVERT_TOKENS.length + 2
        }
      }

      tokenScope = tokenScope.sort((one, other) => getSortValue(one) - getSortValue(other))
    }

    return tokenScope
  }, [tokens, tokensForSelect, searchTerm])

  return (
    <BottomSheet visible={visible} onBackdropClick={onClose}>
      <Stack component={List} spacing={1}>
        <Titlebar title={selectTokenTitle} onActionClick={{ action: onClose, type: 'back' }} />
        <Box px={2}>
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            placeholder={t('common.enterTokenName')}
            autoFocus
          />
        </Box>
        <FixedSizeList
          height={height - 120}
          width="100%"
          itemCount={filteredTokens?.length || 0}
          itemSize={60}
          style={{ overflowX: 'hidden' }}
        >
          {renderToken}
        </FixedSizeList>
      </Stack>
    </BottomSheet>
  )
}

export default TokenPicker
